import axiosInstance from "../../../middlewares/axiosInstance";
import { AxiosResponse } from "axios";
import {
  Product,
  ProductFilters,
  ProductFormData,
  ProductListResponse,
} from "../types/inventory.types";
import { IESDoc } from "../../../app.interface";

interface Filter {
  id: string;
  title: string;
  field: string;
  operator: string;
  value: any;
}

interface ListParams {
  searchQuery?: string;
  filters?: Filter[];
  startDate?: number;
  endDate?: number;
  dateField?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  pageIndex?: number;
  pageSize?: number;
}

interface ListResponse {
  data: IESDoc[];
  metadata: {
    total: {
      value: number;
    };
    took: number;
    timed_out: boolean;
    max_score: number | null;
  };
}

export interface Batch {
  id: string;
  batch_id: string;
  quantity: number;
  unit: string;
  cost_per_unit: number;
  total_cost: number;
  batch_number: string;
  remaining_quantity: number;
  expiry_date: string;
  storage_place_name: string;
  storage_place_id: string;
  product_id: string;
  product_name: string;
  product_type: string;
  product_unit: string;
  product_cost_per_unit: number;
  product_total_cost: number;
  product_remaining_quantity: number;
  product_expiry_date: string;
  product_storage_place_name: string;
  product_storage_place_id: string;
  product_category: string;
  product_description: string;
  product_brand: string;
  product_model: string;
  product_sku: string;
  product_barcode: string;
  product_tags: string[];
  product_attributes: Record<string, any>;
  product_metadata: Record<string, any>;
  product_created_at: string;
  product_updated_at: string;
  created_at: string;
  updated_at: string;
}

export interface ListBatchesParams {
  ingredient_id?: string;
  product_id?: string;
  pageIndex: number;
  pageSize: number;
  sortField: string;
  sortOrder: string;
  include_wasted?: boolean;
  searchQuery?: string;
  filters?: any;
  startDate?: number;
  endDate?: number;
  dateField?: string;
}

export interface ListBatchesResponse {
  batches: Batch[];
  total: number;
}

export interface WasteBatchParams {
  _id: string;
  quantity: number;
  reason: string;
  notes?: string;
}

export interface RecoverBatchParams {
  batch_id: string;
  quantity: number;
}

export interface UseIngredientsRequest {
  ingredients: {
    _id: string;
    batch_id: string;
    quantity: number;
    unit: string;
    notes?: string;
  }[];
}

class InventoryService {
  // Products
  async listSubItems(params: ListParams): Promise<ListResponse> {
    const response = await axiosInstance.post("/business-admin/products/list", {
      ...params,
    });
    return response.data.data;
  }

  async getSubItem(id: string): Promise<AxiosResponse<{ data: Product }>> {
    return axiosInstance.post("/business-admin/products/get", { id });
  }

  async createSubItems(data: ProductFormData): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/products/create", data);
  }

  async updateSubItems(
    id: string,
    data: Partial<Product>
  ): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/products/update", {
      id,
      ...data,
    });
  }

  async deleteSubItems(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/products/delete", { id });
  }

  // Ingredients
  async listIngredients(params: ListParams): Promise<ListResponse> {
    const response = await axiosInstance.post(
      "/business-admin/ingredients/list",
      {
        ...params,
      }
    );
    return response.data.data;
  }

  async getIngredient(id: string): Promise<AxiosResponse<{ data: Product }>> {
    return axiosInstance.post("/business-admin/ingredients/get", { _id: id });
  }

  async createIngredient(data: ProductFormData): Promise<AxiosResponse<any>> {
    const datatosend = {
      ...data,
      base_unit: undefined,
      conversion_factor: undefined,
      is_composite: undefined,
      is_ingredient: undefined,
      price: undefined,
      type: undefined,
    };
    return axiosInstance.post("/business-admin/ingredients/create", datatosend);
  }

  async updateIngredient(
    id: string,
    data: Partial<Product>
  ): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/ingredients/update", {
      _id: id,
      ...data,
    });
  }

  async deleteIngredient(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/ingredients/delete", {
      _id: id,
    });
  }

  // Supplies
  async listSupplies(params: ListParams): Promise<ListResponse> {
    const response = await axiosInstance.post("/business-admin/supplies/list", {
      ...params,
    });
    return response.data.data;
  }

  async getSupply(id: string): Promise<AxiosResponse<{ data: Product }>> {
    return axiosInstance.post("/business-admin/supplies/get", { id });
  }

  async createSupply(data: ProductFormData): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/supplies/create", data);
  }

  async updateSupply(
    id: string,
    data: Partial<Product>
  ): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/supplies/update", {
      id,
      ...data,
    });
  }

  async deleteSupply(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/supplies/delete", { id });
  }

  // Stock Movements
  async getStockMovements(
    productId: string,
    startDate?: number,
    endDate?: number,
    page?: number,
    limit?: number
  ): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/stock-movements", {
      productId,
      startDate,
      endDate,
      page,
      limit,
    });
  }

  async recordStockMovement(
    productId: string,
    quantity: number,
    movementType: "IN" | "OUT" | "TRANSFER",
    sourceLocation?: string,
    destinationLocation?: string,
    notes?: string
  ): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/stock/restock", {
      product_id: productId,
      quantity,
      movement_type: movementType,
      source_location: sourceLocation,
      destination_location: destinationLocation,
      notes,
    });
  }

  // Stock Level
  async getStockLevel(productId: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/stock/get", { id: productId });
  }

  // Production
  async startProduction(
    compositeProductId: string,
    quantity: number,
    notes?: string
  ): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/production/start", {
      composite_product_id: compositeProductId,
      quantity,
      notes,
    });
  }

  async completeProduction(productionId: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/production/complete", {
      production_id: productionId,
    });
  }

  async validateProduction(data: {
    composite_product_id: string;
    quantity: number;
  }): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/production/validate", data);
  }

  // Raw Materials
  async getRawMaterial(
    id: string,
    idType: "id" | "ingredient_id"
  ): Promise<AxiosResponse<any>> {
    const response = await axiosInstance.post(
      `/business-admin/ingredients/get`,
      {
        id,
        idType,
      }
    );
    return response.data;
  }

  async getRawMaterials(
    filters: ProductFilters
  ): Promise<AxiosResponse<ProductListResponse>> {
    return axiosInstance.post("/business-admin/ingredients", {
      params: filters,
    });
  }

  async createRawMaterial(data: ProductFormData): Promise<AxiosResponse<any>> {
    const datatosend = {
      ...data,
      price: undefined,
      type: undefined,
      base_unit: undefined,
      conversion_factor: undefined,
      is_active: undefined,
    };
    const response = await axiosInstance.post(
      "/business-admin/ingredients/create",
      datatosend
    );
    return response.data;
  }

  async updateRawMaterial(data: IESDoc): Promise<AxiosResponse<any>> {
    const datatosend = {
      _id: data._id,
      name: data.name,
      category: data.category,
      description: data.description,
      unit: data.unit,
      stock_quantity: data.stock_quantity,
      min_stock_level: data.min_stock_level,
      max_stock_level: data.max_stock_level,
      storage_instructions: data.storage_instructions,
      sku: data.sku,
      allergens: data.allergens,
      batches: data.batches.map((batch: any) => ({
        _id: batch._id,
        batch_number: batch.batch_number,
        quantity: batch.quantity,
        remaining_quantity: batch.remaining_quantity,
        unit: batch.unit,
        cost_per_unit: batch.cost_per_unit,
        total_cost: batch.total_cost,
        expiry_date: batch.expiry_date,
        source_type: batch.source_type,
        source_name: batch.source_name,
        brand_name: batch.brand_name,
        warehouse_branch: batch.warehouse_branch,
        notes: batch.notes,
      })),
    };

    return axiosInstance.post("/business-admin/ingredients/update", {
      ...datatosend,
    });
  }

  async deleteRawMaterial(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/ingredients/delete", { id });
  }

  async listBatches(params: ListBatchesParams) {
    const response = await axiosInstance.post("/business-admin/batches/list", {
      product_id: params?.product_id || params?.ingredient_id,
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      sortField: params.sortField,
      sortOrder: params.sortOrder,
      filters: params.filters,
      include_wasted: false,
    });

    return response.data;
  }

  async wasteBatch(params: WasteBatchParams): Promise<AxiosResponse<void>> {
    return axiosInstance.post("/business-admin/batches/waste", {
      _id: params._id,
      batch_id: params._id,
      quantity: params.quantity,
      reason: params.reason,
      notes: params.notes,
    });
  }

  async recoverBatch(params: RecoverBatchParams): Promise<AxiosResponse<void>> {
    return axiosInstance.post(`/business-admin/batches/recover`, {
      batch_id: params.batch_id,
      quantity: params.quantity,
    });
  }

  async useIngredients(
    params: UseIngredientsRequest
  ): Promise<AxiosResponse<void>> {
    // Skip the API call if there are no ingredients to avoid validation errors
    if (!params.ingredients || params.ingredients.length === 0) {
      // Return a resolved promise with a success response to maintain consistent return type
      return Promise.resolve({
        data: void 0,
        status: 200,
        statusText: "OK",
        headers: {},
        config: { headers: {} as any } as any,
      });
    }
    return axiosInstance.post("/business-admin/ingredients/use", params);
  }

  async recalculateIngredientStock(): Promise<AxiosResponse<void>> {
    return axiosInstance.post("/business-admin/ingredients/recalculate-stock");
  }
}

export const inventoryService = new InventoryService();
